import { API_BASE_URL } from "./api-base.ts";
import type { CommunityPublication, StrategyCommunityFilters } from "./strategy-community.ts";

/** Display snapshots only. Never an authentication, capability or mutation cache. */
export const COMMUNITY_PREVIEW_KEY = "backtest-community-preview-v1";
export const COMMUNITY_PREVIEW_EPOCH = "backtest-community-preview-epoch-v1";
const VIEWER_KEY = "backtest-community-preview-viewer-v1";
export const COMMUNITY_PREVIEW_EVENT = "community-preview-invalidated";
export const COMMUNITY_PREVIEW_TTL = 24 * 60 * 60 * 1000;
export const COMMUNITY_PREVIEW_MAX_BYTES = 2 * 1024 * 1024;
const MAX_ENTRIES = 12;
type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;
type Entry = { key: string; savedAt: number; expiresAt: number; items: CommunityPublication[] };
export type CommunityPreview = { items: CommunityPublication[]; savedAt: number; expiresAt: number };
const numericMetrics = ["netReturnPercent", "maxDrawdownPercent", "winRatePercent", "sharpeRatio", "profitFactor", "totalTrades", "netProfitAmount"] as const;
const fields = ["id", "title", "summary", "creator", "symbol", "timeframe", "executionModel", "status", "publishedAt", "expiresAt", "testedStart", "testedEnd", "metrics", "sparkline", "tags", "favoriteCount", "duplicateCount", "thanksCount", "evidence"];
function object(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : undefined;
}
function text(value: unknown, max: number): value is string {
  return typeof value === "string" && value === value.trim() && Array.from(value).length > 0 && Array.from(value).length <= max && !/[\p{Cc}\p{Cf}\p{Cs}]/u.test(value);
}
function date(value: unknown): value is string { return text(value, 64) && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value)); }
function count(value: unknown): value is number { return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 && value <= 10_000_000; }
function between(value: unknown, min: number, max: number): boolean { return typeof value === "number" && Number.isFinite(value) && value >= min && value <= max; }
function exact(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return Object.keys(value).length === keys.length && keys.every((key) => Object.prototype.hasOwnProperty.call(value, key));
}

/** Explicit public projection: extra response properties and personal flags cannot persist. */
function project(item: CommunityPublication): Record<string, unknown> {
  return {
    id: item.id, title: item.title, summary: item.summary, creator: { id: item.creator.id, displayName: item.creator.displayName },
    symbol: item.symbol, timeframe: item.timeframe, executionModel: item.executionModel, status: item.status,
    publishedAt: item.publishedAt, expiresAt: item.expiresAt, testedStart: item.testedStart, testedEnd: item.testedEnd,
    metrics: Object.fromEntries(numericMetrics.map((key) => [key, item.metrics[key]])), sparkline: item.sparkline,
    tags: item.tags, favoriteCount: item.favoriteCount, duplicateCount: item.duplicateCount, thanksCount: item.thanksCount,
    evidence: { state: item.evidence.state, configurationAttested: item.evidence.configurationAttested,
      independentRerunCount: item.evidence.independentRerunCount },
  };
}

function parseItem(value: unknown, now: number): CommunityPublication | undefined {
  const item = object(value), creator = object(item?.creator), metrics = object(item?.metrics), evidence = object(item?.evidence);
  if (!item || !exact(item, fields) || item.status !== "listed"
    || !text(item.id, 47) || !/^pub_[A-Za-z0-9_-]{43}$/.test(item.id)
    || !text(item.title, 80) || !text(item.summary, 500)
    || !creator || !exact(creator, ["id", "displayName"]) || !text(creator.id, 36) || !/^crt_[A-Za-z0-9_-]{32}$/.test(creator.id)
    || !text(creator.displayName, 40) || !text(item.symbol, 32) || !text(item.timeframe, 8)
    || !["5m", "15m", "30m", "1h", "4h", "1d"].includes(item.timeframe)
    || !["spot", "futures"].includes(String(item.executionModel))
    || !date(item.publishedAt) || !date(item.expiresAt) || Date.parse(item.expiresAt) <= now
    || !date(item.testedStart) || !date(item.testedEnd) || Date.parse(item.testedStart) >= Date.parse(item.testedEnd)
    || !metrics || !exact(metrics, numericMetrics)
    || numericMetrics.some((key) => metrics[key] !== null && (typeof metrics[key] !== "number" || !Number.isFinite(metrics[key])))
    || !between(metrics.netReturnPercent, -1e9, 1e9) || !between(metrics.maxDrawdownPercent, 0, 100)
    || !between(metrics.winRatePercent, 0, 100) || !count(metrics.totalTrades) || metrics.totalTrades === 0
    || (metrics.profitFactor !== null && !between(metrics.profitFactor, 0, 1e9))
    || (metrics.sharpeRatio !== null && !between(metrics.sharpeRatio, -1e6, 1e6))
    || (metrics.netProfitAmount !== null && !between(metrics.netProfitAmount, -1e15, 1e15))
    || !Array.isArray(item.sparkline) || item.sparkline.length < 2 || item.sparkline.length > 48 || item.sparkline[0] !== 0
    || item.sparkline.some((v) => !between(v, -1e9, 1e9))
    || !Array.isArray(item.tags) || item.tags.length > 8 || item.tags.some((v) => !text(v, 24))
    || !count(item.favoriteCount) || !count(item.duplicateCount) || !count(item.thanksCount)
    || !evidence || !exact(evidence, ["state", "configurationAttested", "independentRerunCount"])
    || !["unvalidated", "in_sample", "selection_passed", "locked_holdout_passed", "locked_holdout_failed", "insufficient_evidence", "unknown"].includes(String(evidence.state))
    || typeof evidence.configurationAttested !== "boolean" || !count(evidence.independentRerunCount)) return undefined;
  return {
    ...item, creator, metrics,
    evidence: { ...evidence, reproducible: false, outOfSample: false, serverRerunCount: 0, notes: [] },
    isFavorited: false, isThanked: false, thanksAvailable: false, rules: [], trades: [], versions: [],
  } as unknown as CommunityPublication;
}

export function communityPreviewKey(accountID: string | null | undefined, locale: string, filters: StrategyCommunityFilters, origin = API_BASE_URL): string | undefined {
  if (!["recommended", "verified", "new"].includes(filters.tab) || filters.cursor || !text(locale, 16)
    || (filters.marketID != null && (typeof filters.marketID !== "string" || !/^[a-z0-9][a-z0-9-]{0,79}$/.test(filters.marketID)
      || !["5m", "15m", "30m", "1h", "4h", "1d"].includes(filters.timeframe ?? "")))
    || (accountID != null && !text(accountID, 128))
    || (filters.maxDrawdown != null && (!Number.isFinite(filters.maxDrawdown) || filters.maxDrawdown < 0 || filters.maxDrawdown > 100))
    || (filters.sort != null && !["newest", "lowest_drawdown", "evidence_risk_freshness"].includes(filters.sort))
    || (filters.limit != null && (!Number.isSafeInteger(filters.limit) || filters.limit < 1 || filters.limit > 50))) return undefined;
  try {
    const url = new URL(origin);
    if (!["https:", "http:"].includes(url.protocol) || url.username || url.password) return undefined;
    const dimensions = [filters.tab, filters.query ?? "", filters.market ?? "", filters.timeframe ?? "", filters.evidence ?? "",
      filters.maxDrawdown ?? null, filters.sort ?? (filters.tab === "new" ? "newest" : "evidence_risk_freshness"), filters.limit ?? 24];
    if (dimensions.some((v) => typeof v === "string" && v.length > 128)) return undefined;
    // Preserve existing general-feed keys, while catalog queries retain their
    // exact market identity instead of sharing a legacy symbol-only snapshot.
    return JSON.stringify([url.origin, accountID ?? null, locale, ...dimensions, ...(filters.marketID ? [filters.marketID] : [])]);
  } catch { return undefined; }
}

function validKey(key: unknown): key is string {
  if (!text(key, 1800)) return false;
  try {
    const p = JSON.parse(key);
    return Array.isArray(p) && (p.length === 11 || (p.length === 12 && typeof p[11] === "string" && p[11].length > 0))
      && (p[1] === null || typeof p[1] === "string")
      && [p[0], p[2], p[3], p[4], p[5], p[6], p[7], p[9]].every((v) => typeof v === "string")
      && communityPreviewKey(p[1], p[2], { tab: p[3], query: p[4], market: p[5], timeframe: p[6], evidence: p[7],
        maxDrawdown: p[8] ?? undefined, sort: p[9], limit: p[10], marketID: p[11] }, p[0]) === key;
  } catch { return false; }
}

function matchesMarketKey(key: string, items: CommunityPublication[]): boolean {
  const dimensions = JSON.parse(key);
  return dimensions.length === 11 || items.every((item) => item.executionModel === "futures" && item.timeframe === dimensions[6]);
}

export class CommunityPreviewCache {
  private readonly storage: StorageLike | undefined;
  private readonly now: () => number;
  private storageBlocked = false;
  private memoryEpoch = "";
  constructor(storage: StorageLike | undefined = browserStorage(), now = Date.now) { this.storage = storage; this.now = now; }
  epoch(): string { if (this.storageBlocked) return this.memoryEpoch; try { return this.storage?.getItem(COMMUNITY_PREVIEW_EPOCH) ?? "0"; } catch { return "unavailable"; } }
  isCurrent(epoch: string): boolean { return epoch === this.epoch(); }
  private entries(): Entry[] {
    if (this.storageBlocked) return [];
    try {
      const raw = this.storage?.getItem(COMMUNITY_PREVIEW_KEY);
      if (!raw || raw.length * 2 > COMMUNITY_PREVIEW_MAX_BYTES) return [];
      const value = JSON.parse(raw), now = this.now();
      if (!object(value) || !exact(value, ["version", "epoch", "entries"]) || value.version !== 1
        || value.epoch !== this.epoch() || !Array.isArray(value.entries) || value.entries.length > MAX_ENTRIES) return [];
      return value.entries.flatMap((entry: unknown) => {
        const e = object(entry);
        if (!e || !exact(e, ["key", "savedAt", "expiresAt", "items"]) || !validKey(e.key)
          || typeof e.savedAt !== "number" || typeof e.expiresAt !== "number" || !Number.isFinite(e.savedAt) || !Number.isFinite(e.expiresAt)
          || e.savedAt > now || e.savedAt <= 0 || e.expiresAt <= now || e.expiresAt <= e.savedAt || e.expiresAt - e.savedAt > COMMUNITY_PREVIEW_TTL
          || !Array.isArray(e.items) || !e.items.length || e.items.length > 24) return [];
        const items = e.items.map((item) => parseItem(item, now));
        if (items.some((item) => !item || Date.parse(item.expiresAt!) < (e.expiresAt as number))
          || !matchesMarketKey(e.key, items as CommunityPublication[])
          || new Set(items.map((item) => item?.id)).size !== items.length) return [];
        return [{ key: e.key, savedAt: e.savedAt, expiresAt: e.expiresAt, items: items as CommunityPublication[] }];
      });
    } catch { return []; }
  }
  read(key: string | undefined): CommunityPreview | undefined {
    const entry = validKey(key) ? this.entries().find((entry) => entry.key === key) : undefined;
    return entry && { items: entry.items, savedAt: entry.savedAt, expiresAt: entry.expiresAt };
  }
  write(key: string | undefined, items: CommunityPublication[], epoch: string): boolean {
    if (!validKey(key) || !this.storage || this.storageBlocked || !this.isCurrent(epoch)) return false;
    try {
      const now = this.now(), projected = items.slice(0, 24).map(project);
      const parsed = projected.map((item) => parseItem(item, now));
      if (parsed.some((item) => !item) || !matchesMarketKey(key, parsed as CommunityPublication[])) return false;
      const entry = { key, savedAt: now, expiresAt: Math.min(now + COMMUNITY_PREVIEW_TTL, ...parsed.map((item) => Date.parse(item!.expiresAt!))), items: projected };
      const entries = [entry, ...this.entries().filter((entry) => entry.key !== key).map((entry) => ({ ...entry, items: entry.items.map(project) }))]
        .filter((entry) => entry.items.length).slice(0, MAX_ENTRIES);
      let raw = JSON.stringify({ version: 1, epoch, entries });
      while (raw.length * 2 > COMMUNITY_PREVIEW_MAX_BYTES && entries.length) {
        entries.pop(); raw = JSON.stringify({ version: 1, epoch, entries });
      }
      if (!this.isCurrent(epoch)) return false;
      this.storage.setItem(COMMUNITY_PREVIEW_KEY, raw);
      return this.isCurrent(epoch);
    } catch { return false; }
  }
  invalidate(): void {
    this.memoryEpoch = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
    this.storageBlocked = true;
    try {
      this.storage?.setItem(COMMUNITY_PREVIEW_EPOCH, this.memoryEpoch);
      this.storageBlocked = false;
    } catch { /* Fence this instance even if writing the shared epoch failed. */ }
    try { this.storage?.removeItem(COMMUNITY_PREVIEW_KEY); } catch { /* Removal is independent of epoch persistence. */ }
    if (typeof window !== "undefined") window.dispatchEvent(new Event(COMMUNITY_PREVIEW_EVENT));
  }
  /** First identity resolution preserves same-user restart; real transitions purge. */
  resolveViewer(accountID: string | null): void {
    try {
      const next = JSON.stringify(accountID), previous = this.storage?.getItem(VIEWER_KEY);
      if (previous !== null && previous !== undefined && previous !== next) this.invalidate();
      this.storage?.setItem(VIEWER_KEY, next);
    } catch { /* no storage means no persistent preview */ }
  }
}
function browserStorage(): StorageLike | undefined { try { return globalThis.localStorage; } catch { return undefined; } }
export const communityPreviewCache = new CommunityPreviewCache();
export function communityPreviewInvalidationEvent(event: Event): boolean {
  return event.type === COMMUNITY_PREVIEW_EVENT || (typeof StorageEvent !== "undefined" && event instanceof StorageEvent
    && (event.key === COMMUNITY_PREVIEW_EPOCH || (event.key === COMMUNITY_PREVIEW_KEY && event.newValue === null) || event.key === null));
}
